import { builderCompleteness, builderDeadEnds, builderLoose, isAside, isStartOrEnd, titleCase, type BuilderNode, type BuilderTree } from '@nsb/core';
import { bandOf } from './bands.ts';
import { tileWords, type Band, type TilePhase, type TileStep } from './model.ts';

/**
 * The master tree (Settings → Process tree) as tiles: every step on the board, its band by who does it (study 10, band
 * B), its picture always, marked "Not configured" with a ring until everything its kind needs is filled in (Jason,
 * 2026-10-07: "clearly highlight unconfigured steps in any tree and always show its icon"; look.ts), the bar along its
 * foot saying how much is, red or blue while it is joined up (nothing comes in, leads to nothing); each
 * phase's heading with its count, the gate that closes it, how sure the price is after it and its share of the time.
 */

const START_SPREAD_WORDS = (spread: number, sureAfter: number) => `±${Math.round(spread * sureAfter * 100)}% after it`;

/** A phase's share of the time, among its heading's facts. */
export const SHARE_WORDS = (share: number) => `${Math.round(share)}% of the time`;

/** Who does a step of the master tree, as its band says it. */
export const masterBand = (n: BuilderNode, tradeName: (key: string) => string | null): Band =>
  bandOf({ kind: n.kind === 'task' ? 'work' : n.kind, who: n.who, trade: n.trade }, 'nsb', tradeName);

/** The steps on the board: placed, at the top of the tree (a task is in its step's list), out of the bin. */
export const onBoard = (t: Pick<BuilderTree, 'nodes'>): BuilderNode[] => t.nodes.filter((n) => n.parent === null && n.at !== null && !isAside(n));

export function masterTiles(t: BuilderTree, pictures: ReadonlyMap<string, string>, tradeName: (key: string) => string | null): { phases: TilePhase[]; steps: TileStep[] } {
  const placed = onBoard(t);
  const loose = builderLoose(t);
  const dead = builderDeadEnds(t);
  const known = new Set(t.phases.map((p) => p.key));
  const byKey = new Map(t.nodes.map((n) => [n.key, n]));
  const steps: TileStep[] = placed.map((n) => {
    const c = builderCompleteness(t, n);
    const band = masterBand(n, tradeName);
    return {
      key: n.key,
      phase: n.phase !== null && known.has(n.phase) ? n.phase : null,
      needs: [...n.dependsOn],
      ...(isStartOrEnd(n) ? { end: n.kind === 'start' ? ('start' as const) : ('end' as const) } : {}),
      title: n.title,
      band,
      picture: pictures.get(n.key) ?? null,
      configured: c.configured,
      complete: { done: c.done, of: c.of },
      highlight: loose.has(n.key) ? 'loose' : dead.has(n.key) ? 'dead' : null,
      options: n.kind === 'decision' && n.options.length ? n.options.map((o) => ({ option: o.id, text: o.text })) : null,
      words: tileWords(n.title, band, c.configured ? 'Configured' : `Not configured: ${c.done} of ${c.of} set; missing: ${c.missing.join(', ')}`),
    };
  });
  const phases: TilePhase[] = t.phases.map((p) => {
    const count = steps.filter((s) => s.phase === p.key && !s.end).length;
    const closer = p.closedBy ? byKey.get(p.closedBy) : undefined;
    return {
      key: p.key,
      name: p.name,
      what: p.what,
      closedBy: p.closedBy,
      facts: [
        `${count} ${count === 1 ? 'step' : 'steps'}`,
        closer ? `ends with ${titleCase(closer.title)}` : 'ends with its last step',
        START_SPREAD_WORDS(t.startSpread, p.sureAfter),
        // Its share of the time, set on its card (the plan of 2026-10-07, item 18); none set, none shown.
        typeof p.share === 'number' ? SHARE_WORDS(p.share) : '',
      ].filter(Boolean),
    };
  });
  return { phases, steps };
}
